import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import type { AnalysisStepVerdict } from '../../types/assignmentQuality';
import '@shared-ui/parsed-feedback/parsed-feedback.css';

const TONE: Record<string, string> = { good: 'ok', acceptable: 'warn', bad: 'bad' };

function Badge({ value, label }: { value?: string; label?: string }) {
  if (!value) return null;
  return <span className={`jv-badge jv-${TONE[value] ?? 'neutral'}`}>{label ? `${label}: ${value}` : value}</span>;
}

/**
 * What the judge (or a human) made of this analysis, step by step.
 *
 * The tables collapse this to one word per analysis because a cell has room for
 * one. Here every step keeps its own verdict — a reading step judged `bad`
 * under an assessment judged `good` IS the diagnosis, and it is invisible
 * anywhere else.
 *
 * Renders nothing when nothing has judged this analysis: an empty section
 * inviting the reader to wonder what it is for is worse than its absence.
 */
export function AnalysisJudgeVerdicts({ submissionId }: { submissionId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['analysisVerdicts', submissionId],
    queryFn: () => adminApi.getAnalysisVerdicts(submissionId),
    enabled: !!submissionId,
  });

  const verdicts = data?.verdicts ?? [];
  if (isLoading || verdicts.length === 0) return null;

  return (
    <div className="detail-section">
      <h3>Judged quality</h3>
      <div className="jv">
        {verdicts.map((v: AnalysisStepVerdict) => (
          <div key={`${v.outputId}:${v.evaluator}`} className="jv-item">
            <div className="jv-verdicts">
              <span className="jv-tag">step {v.stepIndex + 1} · {v.stepType ?? 'unknown'} · {v.role}</span>
              <Badge value={v.verdict} />
              {/* Only when it differs: equal to `verdict` it says nothing, and a
                  gap between them is the finding — this step reasoned well from
                  a transcription it should not have been given. */}
              {v.conditionalVerdict && v.conditionalVerdict !== v.verdict && (
                <Badge value={v.conditionalVerdict} label="given transcription" />
              )}
              <Badge value={v.accuracy} label="accuracy" />
              <Badge value={v.approach} label="approach" />
              {v.evaluator === 'human' && <span className="jv-tag">human</span>}
              {v.stale && <span className="jv-badge jv-warn">stale</span>}
              {v.failureTags.map(tag => <span key={tag} className="jv-tag">{tag}</span>)}
            </div>

            {v.critique && <p className="jv-reason">{v.critique}</p>}
            {v.accuracyReason && <p className="jv-reason"><b>Accuracy:</b> {v.accuracyReason}</p>}
            {v.approachReason && <p className="jv-reason"><b>Approach:</b> {v.approachReason}</p>}

            <code className="jv-item-id">
              {v.model ?? v.provider ?? 'unknown model'}
              {v.promptId ? ` · ${v.promptId}@${v.promptVersion ?? '?'}` : ''}
              {` · judged ${v.at.slice(0, 10)}`}
            </code>
          </div>
        ))}
      </div>
    </div>
  );
}
